<template>
    <div class="quotation-view-page">
        <app-sub-title text="Ver Cotización" />
        
        <div class="quotation-form-container">
            <div class="form-section mb-4">
                <h5>Cliente</h5>
                <div v-if="quotation.client" class="client-info p-3 bg-light rounded">
                    <h6>Información del Cliente</h6>
                    <div class="row">
                        <div class="col-md-6">
                            <p><strong>Nombre:</strong> {{ quotation.client.name }}</p>
                            <p><strong>Documento:</strong> {{ quotation.client.document || 'N/A' }}</p>
                        </div>
                        <div class="col-md-6">
                            <p><strong>Email:</strong> {{ quotation.client.email || 'N/A' }}</p>
                            <p><strong>Teléfono:</strong> {{ quotation.client.phone || 'N/A' }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-section mb-4">
                <h5>Información General</h5>
                <div class="row mb-2">
                    <div class="col-md-12">
                        <p><strong>Título:</strong> {{ quotation.title }}</p>
                    </div>
                </div>
                <div class="row mb-2" v-if="quotation.description">
                    <div class="col-md-12">
                        <p><strong>Descripción:</strong> {{ quotation.description }}</p>
                    </div>
                </div>
                <div class="row mb-2">
                    <div class="col-md-6">
                        <p><strong>Estado:</strong> 
                            <span class="badge" :class="getStatusBadgeClass(quotation.status)">
                                {{ getStatusLabel(quotation.status) }}
                            </span>
                        </p>
                    </div>
                    <div class="col-md-6">
                        <p><strong>TRM Usado:</strong> {{ quotation.trm_used ? formatCurrency(quotation.trm_used) : 'N/A' }}</p>
                    </div>
                </div>
                <div class="row mb-2" v-if="quotation.code">
                    <div class="col-md-12">
                        <p><strong>Código:</strong> <span class="badge badge-primary">{{ quotation.code }}</span></p>
                    </div>
                </div>
            </div>

            <!-- Categories Accordion -->
            <div class="form-section mb-4">
                <h5>Items de Cotización</h5>
                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-equipment-cctv variant="info">
                            Equipos sistemas de CCTV
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('equipment-cctv') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-equipment-cctv" visible accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <quotation-lines-view
                                category-slug="equipment-cctv"
                                category-name="Equipos sistemas de CCTV"
                                :lines="quotationLines['equipment-cctv']"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-equipment-access-control variant="info">
                            Equipos sistema control de acceso
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('equipment-access-control') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-equipment-access-control" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <quotation-lines-view
                                category-slug="equipment-access-control"
                                category-name="Equipos sistema control de acceso"
                                :lines="quotationLines['equipment-access-control']"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-equipment-network variant="info">
                            Equipos de red
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('equipment-network') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-equipment-network" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <quotation-lines-view
                                category-slug="equipment-network"
                                category-name="Equipos de red"
                                :lines="quotationLines['equipment-network']"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-equipment-alarm variant="info">
                            Equipos sistema de alarma
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('equipment-alarm') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-equipment-alarm" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <quotation-lines-view
                                category-slug="equipment-alarm"
                                category-name="Equipos sistema de alarma"
                                :lines="quotationLines['equipment-alarm']"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-material variant="info">
                            Materiales
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('material') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-material" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <quotation-lines-view
                                category-slug="material"
                                category-name="Materiales"
                                :lines="quotationLines.material"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-labor variant="info">
                            Mano de Obra
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('labor') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-labor" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <quotation-lines-view
                                category-slug="labor"
                                category-name="Mano de Obra"
                                :lines="quotationLines.labor"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-license variant="info">
                            Licencia
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('license') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-license" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <quotation-lines-view
                                category-slug="license"
                                category-name="Licencia"
                                :lines="quotationLines.license"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>

                <b-card no-body class="mb-2">
                    <b-card-header header-tag="header" class="p-0" role="tab">
                        <b-button block v-b-toggle.accordion-additional variant="info">
                            Adicional
                            <span class="badge badge-light ml-2">{{ getCategorySubtotal('additional') | currency }}</span>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion-additional" accordion="items-accordion" role="tabpanel">
                        <b-card-body>
                            <quotation-lines-view
                                category-slug="additional"
                                category-name="Adicional"
                                :lines="quotationLines.additional"
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>
            </div>

            <div class="form-section mb-4">
                <h5>Totales</h5>
                <div class="totals-container p-3 bg-light rounded">
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Subtotal General:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ subtotalGeneral | currency }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>IVA 19%:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ iva | currency }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Descuento:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ discount | currency }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Total (con descuento):</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong class="text-primary" style="font-size: 1.2em;">{{ total | currency }}</strong>
                        </div>
                    </div>
                    <hr class="my-2">
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Costo Total del Proyecto:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ totalCost | currency }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2" v-if="quotation.installation_hours">
                        <div class="col-md-6">
                            <strong>Horas de Instalación:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ parseFloat(quotation.installation_hours).toFixed(2) }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2" v-if="quotation.installation_hours">
                        <div class="col-md-6">
                            <strong>Días de Instalación:</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong>{{ installationDays.toFixed(2) }}</strong>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col-md-6">
                            <strong>Rentabilidad / Utilidad (sin IVA):</strong>
                        </div>
                        <div class="col-md-6 text-right">
                            <strong :class="profitability >= 0 ? 'text-success' : 'text-danger'">
                                {{ profitability | currency }}
                                <span v-if="profitabilityPercentage !== 0" class="ml-2">
                                    ({{ profitabilityPercentage >= 0 ? '+' : '' }}{{ profitabilityPercentage.toFixed(2) }}%)
                                </span>
                            </strong>
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-actions">
                <button class="btn btn-secondary" @click="goBack">
                    <i class="fa fa-arrow-left"></i> Volver
                </button>
                <button class="btn btn-primary" @click="openExportModal">
                    <i class="fa fa-download"></i> Descargar Cotización
                </button>
                <button class="btn btn-info" @click="openRentingModal">
                    <i class="fa fa-file-text-o"></i> Descargar Renting
                </button>
            </div>
        </div>

        <!-- Export PDF Modal -->
        <b-modal
            id="exportPdfModal"
            ref="exportPdfModal"
            title="Descargar Cotización"
            @ok="handleExportPdf"
            ok-title="Descargar"
            cancel-title="Cancelar"
            :ok-disabled="!isValidForm"
        >
            <div class="form-group">
                <label for="validity_days">Días de validez de la oferta <span class="text-danger">*</span></label>
                <input
                    type="number"
                    class="form-control"
                    id="validity_days"
                    v-model.number="exportForm.validity_days"
                    min="1"
                    required
                />
                <small class="form-text text-muted">Ejemplo: 30 días</small>
            </div>
            <div class="form-group mt-3">
                <label for="advance_percentage">Porcentaje de anticipo (%) <span class="text-danger">*</span></label>
                <input
                    type="number"
                    class="form-control"
                    id="advance_percentage"
                    v-model.number="exportForm.advance_percentage"
                    min="1"
                    max="100"
                    required
                />
                <small class="form-text text-muted">Ejemplo: 60% para anticipo, 40% restante contra entrega</small>
            </div>
            <div class="form-group mt-3">
                <label for="installation_requirements">Requerimientos mínimos del cliente</label>
                <textarea
                    class="form-control"
                    id="installation_requirements"
                    v-model="exportForm.installation_requirements"
                    rows="4"
                    placeholder="Ej: Suministrar punto eléctrico 110AC, internet de 100 megas con IP fija..."
                ></textarea>
                <small class="form-text text-muted">Requerimientos con los que debe contar el cliente para la instalación</small>
            </div>
            <div class="alert alert-info mt-3" v-if="exportForm.advance_percentage && total > 0">
                <strong>Vista previa de pago:</strong><br>
                Anticipo del {{ exportForm.advance_percentage }}%: {{ calculateAdvance | currency }}<br>
                Saldo restante del {{ 100 - exportForm.advance_percentage }}%: {{ calculateRemaining | currency }}
            </div>
        </b-modal>

        <!-- Export PDF to Dues (Renting) Modal -->
        <b-modal
            id="exportRentingModal"
            ref="exportRentingModal"
            title="Descargar Cotización a Cuotas (Renting)"
            @ok="handleExportRenting"
            ok-title="Descargar"
            cancel-title="Cancelar"
            :ok-disabled="!isValidRentingForm"
        >
            <div class="form-group">
                <label for="renting_interest_rate">Tasa de interés % (EA) <span class="text-danger">*</span></label>
                <input
                    type="number"
                    class="form-control"
                    id="renting_interest_rate"
                    v-model.number="rentingForm.interest_rate"
                    min="1"
                    required
                />
                <small class="form-text text-muted">Ejemplo: 36 para 36% efectivo anual</small>
            </div>
            <div class="form-group mt-3">
                <label for="renting_plazo_meses">Plazo (meses) <span class="text-danger">*</span></label>
                <input
                    type="number"
                    class="form-control"
                    id="renting_plazo_meses"
                    v-model.number="rentingForm.plazo_meses"
                    min="1"
                    required
                />
                <small class="form-text text-muted">Ejemplo: 12 para 12 meses</small>
            </div>
            <div class="form-group mt-3">
                <label for="renting_installation_requirements">Requerimientos mínimos del cliente</label>
                <textarea
                    class="form-control"
                    id="renting_installation_requirements"
                    v-model="rentingForm.installation_requirements"
                    rows="4"
                    placeholder="Ej: Suministrar punto eléctrico 110AC, internet de 100 megas con IP fija..."
                ></textarea>
                <small class="form-text text-muted">Requerimientos con los que debe contar el cliente para la instalación</small>
            </div>
        </b-modal>
    </div>
</template>

<script>
import SubTitle from '../../UI/Layout/SubTitle.vue';
import QuotationLinesView from './QuotationLinesView/QuotationLinesView.vue';
import quotationsRepository from '../../../repositories/quotations.repository';

export default {
    components: {
        appSubTitle: SubTitle,
        quotationLinesView: QuotationLinesView
    },
    data() {
        return {
            quotation: {
                client: null,
                title: '',
                description: '',
                status: '',
                trm_used: null,
                total_cop: null,
                discount: 0
            },
            quotationLines: {
                'equipment-cctv': [],
                'equipment-access-control': [],
                'equipment-network': [],
                'equipment-alarm': [],
                material: [],
                labor: [],
                license: [],
                additional: []
            },
            loading: true,
            exportForm: {
                validity_days: 30,
                advance_percentage: 60,
                installation_requirements: ''
            },
            rentingForm: {
                interest_rate: null,
                plazo_meses: null,
                installation_requirements: ''
            }
        };
    },
    computed: {
        subtotalGeneral() {
            let total = 0;
            Object.keys(this.quotationLines).forEach(category => {
                this.quotationLines[category].forEach(line => {
                    total += parseFloat(line.subtotal_after_multiplier) || 0;
                });
            });
            return total || parseFloat(this.quotation.total_cop) || 0;
        },
        discount() {
            return parseFloat(this.quotation.discount) || 0;
        },
        salePrice() {
            return Math.max(this.subtotalGeneral - this.discount, 0);
        },
        iva() {
            return this.salePrice * 0.19;
        },
        total() {
            return this.salePrice + this.iva;
        },
        totalCost() {
            let cost = 0;
            Object.keys(this.quotationLines).forEach(category => {
                this.quotationLines[category].forEach(line => {
                    cost += parseFloat(line.subtotal) || 0;
                });
            });
            return cost;
        },
        profitability() {
            return this.salePrice - this.totalCost;
        },
        profitabilityPercentage() {
            if (this.salePrice === 0) return 0;
            return (this.profitability / this.salePrice) * 100;
        },
        installationDays() {
            if (!this.quotation.installation_hours) return 0;
            return parseFloat(this.quotation.installation_hours) / 8;
        },
        isValidForm() {
            return this.exportForm.validity_days > 0 &&
                   this.exportForm.advance_percentage > 0 &&
                   this.exportForm.advance_percentage <= 100;
        },
        isValidRentingForm() {
            return (this.rentingForm.interest_rate !== null && this.rentingForm.interest_rate > 0) &&
                   (this.rentingForm.plazo_meses !== null && this.rentingForm.plazo_meses > 0);
        },
        calculateAdvance() {
            return (this.total * this.exportForm.advance_percentage) / 100;
        },
        calculateRemaining() {
            return this.total - this.calculateAdvance;
        }
    },
    created() {
        this.loadQuotation();
    },
    methods: {
        loadQuotation() {
            const id = this.$route.params.id;
            if (!id) {
                toastr.error('ID de cotización no válido');
                this.goBack();
                return;
            }

            this.loading = true;
            const pr = { ...quotationsRepository };
            pr.getQuotation(id)
                .then(response => {
                    if (response) {
                        this.quotation = response;
                        this.organizeLinesByCategory(response.lines || []);
                    } else {
                        toastr.error('No se pudo cargar la cotización');
                        this.goBack();
                    }
                })
                .catch(error => {
                    toastr.error('Error al cargar la cotización');
                    console.error(error);
                    this.goBack();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        organizeLinesByCategory(lines) {
            const categories = [
                'equipment-cctv',
                'equipment-access-control',
                'equipment-network',
                'equipment-alarm',
                'material',
                'labor',
                'license',
                'additional'
            ];
            categories.forEach(categorySlug => {
                this.quotationLines[categorySlug] = [];
            });

            lines.forEach(line => {
                let categorySlug =
                    (line.item && line.item.category && line.item.category.slug) ||
                    (line.category && line.category.slug) ||
                    null;

                // Si la categoría es 'equipment' (antigua), mapearla a 'equipment-cctv' por compatibilidad
                if (categorySlug === 'equipment') {
                    categorySlug = 'equipment-cctv';
                }

                if (categorySlug && this.quotationLines[categorySlug] !== undefined) {
                    this.quotationLines[categorySlug].push(line);
                }
            });
        },
        getCategorySubtotal(categorySlug) {
            let total = 0;
            if (this.quotationLines[categorySlug]) {
                this.quotationLines[categorySlug].forEach(line => {
                    total += parseFloat(line.subtotal_after_multiplier) || 0;
                });
            }
            return total;
        },
        getStatusLabel(status) {
            const statusMap = {
                'draft': 'Borrador',
                'sent': 'Enviada',
                'approved': 'Aprobada',
                'rejected': 'Rechazada',
                'canceled': 'Cancelada'
            };
            return statusMap[status] || status;
        },
        getStatusBadgeClass(status) {
            const classMap = {
                'draft': 'badge-secondary',
                'sent': 'badge-info',
                'approved': 'badge-success',
                'rejected': 'badge-danger',
                'canceled': 'badge-warning'
            };
            return classMap[status] || 'badge-secondary';
        },
        formatCurrency(value) {
            if (!value && value !== 0) return '$ 0,00';
            return new Intl.NumberFormat('es-CO', {
                style: 'currency',
                currency: 'COP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        },
        openExportModal() {
            this.$refs.exportPdfModal.show();
        },
        handleExportPdf(bvModalEvt) {
            bvModalEvt.preventDefault();

            if (!this.isValidForm) {
                toastr.error('Por favor complete todos los campos requeridos');
                return;
            }

            const id = this.$route.params.id;
            const url = `/web/v1/quotations/${id}/export-pdf`;

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = url;
            form.style.display = 'none';

            const csrfToken = document.querySelector('meta[name="csrf-token"]');
            if (csrfToken) {
                const tokenInput = document.createElement('input');
                tokenInput.type = 'hidden';
                tokenInput.name = '_token';
                tokenInput.value = csrfToken.getAttribute('content');
                form.appendChild(tokenInput);
            }

            const fields = {
                validity_days: this.exportForm.validity_days,
                advance_percentage: this.exportForm.advance_percentage,
                installation_requirements: this.exportForm.installation_requirements
            };

            Object.keys(fields).forEach(key => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = fields[key];
                form.appendChild(input);
            });

            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);

            toastr.success('Descargando cotización...');
            this.$refs.exportPdfModal.hide();
        },
        openRentingModal() {
            this.$refs.exportRentingModal.show();
        },
        handleExportRenting(bvModalEvt) {
            bvModalEvt.preventDefault();
            if (!this.isValidRentingForm) {
                toastr.error('Por favor complete tasa de interés y plazo (meses).');
                return;
            }
            const id = this.$route.params.id;
            const url = `/web/v1/quotations/${id}/export-pdf-to-dues`;
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = url;
            form.style.display = 'none';
            const csrfToken = document.querySelector('meta[name="csrf-token"]');
            if (csrfToken) {
                const tokenInput = document.createElement('input');
                tokenInput.type = 'hidden';
                tokenInput.name = '_token';
                tokenInput.value = csrfToken.getAttribute('content');
                form.appendChild(tokenInput);
            }
            const fields = {
                interest_rate: this.rentingForm.interest_rate,
                plazo_meses: this.rentingForm.plazo_meses,
                installation_requirements: this.rentingForm.installation_requirements || ''
            };
            Object.keys(fields).forEach(key => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = fields[key];
                form.appendChild(input);
            });
            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
            toastr.success('Descargando cotización a cuotas...');
            this.$refs.exportRentingModal.hide();
        },
        goBack() {
            this.$router.push({ name: 'quotationsPage' });
        }
    },
    filters: {
        currency(value) {
            if (!value && value !== 0) return '$ 0,00';
            return new Intl.NumberFormat('es-CO', {
                style: 'currency',
                currency: 'COP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(value);
        }
    }
};
</script>

<style scoped>
.quotation-view-page {
    padding: 20px;
}

.form-section {
    background: #fff;
    padding: 20px;
    border-radius: 5px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.client-info {
    border: 1px solid #dee2e6;
}

.totals-container {
    border: 2px solid #007bff;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}
</style>
